import React, { useMemo, useState } from 'react';
import { Colors, ChartColors } from '../../../constants/Colors.js';
import EmptyStateChart from '../empty-state-chart.js';

/**
 * Mapa de calor de cooperativas (renglones) por semana ISO (columnas).
 *
 * Los cortes son fijos y no se recalculan con los datos a la vista, para que el
 * mismo color signifique lo mismo entre ciclos y entre filtros.
 *
 * semanas:  [{ yw, label, mes, anio }]
 * filas:    [{ id_cooperativa, cooperativa, counts: { yw: n }, total }]
 * cortes:   4 umbrales ascendentes; definen los 5 escalones de la rampa
 * formato:  (valor, fila) => string a pintar en la celda
 * resumen:  { header, valor: fila => string } columna final opcional
 */
const UsoSirioHeatmap = ({
  semanas,
  filas,
  cortes,
  formato = valor => valor,
  resumen,
  leyenda,
  ordenarPor = fila => fila.total,
  ocultarVaciasLabel = 'Sólo con actividad',
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [soloConActividad, setSoloConActividad] = useState(true);
  const [ordenarPorTotal, setOrdenarPorTotal] = useState(false);

  // -1 = sin actividad; 0..cortes.length = escalón de la rampa
  const escalon = valor => {
    if (!valor) return -1;
    const indice = cortes.findIndex(corte => valor <= corte);
    return indice === -1 ? cortes.length : indice;
  };

  const visibles = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    let resultado = filas;
    if (termino) {
      resultado = resultado.filter(fila => fila.cooperativa.toLowerCase().includes(termino));
    }
    if (soloConActividad) {
      resultado = resultado.filter(fila => Object.keys(fila.counts || {}).length > 0);
    }
    return ordenarPorTotal ? [...resultado].sort((a, b) => ordenarPor(b) - ordenarPor(a)) : resultado;
  }, [filas, busqueda, soloConActividad, ordenarPorTotal, ordenarPor]);

  // Un encabezado por mes, abarcando sus semanas
  const meses = useMemo(() => {
    const grupos = [];
    semanas.forEach(semana => {
      const etiqueta = `${semana.mes} ${semana.anio}`;
      const ultimo = grupos[grupos.length - 1];
      if (ultimo && ultimo.etiqueta === etiqueta) {
        ultimo.span += 1;
      } else {
        grupos.push({ etiqueta, span: 1 });
      }
    });
    return grupos;
  }, [semanas]);

  if (!semanas.length) {
    return <EmptyStateChart message="No hay actividad registrada en este ciclo" />;
  }

  const celdaBase = {
    textAlign: 'center',
    fontSize: 11,
    padding: '3px 0',
    minWidth: 34,
    border: `1px solid ${Colors.white}`,
  };

  const coopBase = {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    backgroundColor: Colors.white,
    fontSize: 12,
    padding: '3px 10px 3px 0',
    minWidth: 190,
    maxWidth: 190,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 10 }}>
        <input
          type="text"
          className="form-control select-ciclo"
          style={{ maxWidth: 240 }}
          placeholder="Buscar cooperativa…"
          value={busqueda}
          onChange={event => setBusqueda(event.target.value)}
        />
        <button
          type="button"
          className="btn btn-light"
          style={{
            borderRadius: 10,
            borderColor: Colors.neutral_100,
            backgroundColor: soloConActividad ? Colors.neutral_3 : 'transparent',
            fontSize: '0.8rem',
          }}
          onClick={() => setSoloConActividad(valor => !valor)}>
          {ocultarVaciasLabel}
        </button>
        <button
          type="button"
          className="btn btn-light"
          style={{
            borderRadius: 10,
            borderColor: Colors.neutral_100,
            backgroundColor: ordenarPorTotal ? Colors.neutral_3 : 'transparent',
            fontSize: '0.8rem',
          }}
          onClick={() => setOrdenarPorTotal(valor => !valor)}>
          Ordenar por total
        </button>

        <div className="d-flex align-items-center ml-auto" style={{ gap: 4, fontSize: 11, color: Colors.gray_base }}>
          <span>{leyenda?.min ?? 'Menos'}</span>
          {ChartColors.sequential.map((color, i) => (
            <span
              key={color}
              title={
                i === 0
                  ? `1 – ${cortes[0]}`
                  : i === ChartColors.sequential.length - 1
                  ? `más de ${cortes[cortes.length - 1]}`
                  : `${cortes[i - 1] + 1} – ${cortes[i]}`
              }
              style={{ width: 16, height: 12, borderRadius: 2, backgroundColor: color, display: 'inline-block' }}
            />
          ))}
          <span>{leyenda?.max ?? 'Más'}</span>
        </div>
      </div>

      {visibles.length === 0 ? (
        <EmptyStateChart message="Ninguna cooperativa coincide con el filtro" />
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: 560, overflowY: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                <th style={{ ...coopBase, zIndex: 4, position: 'sticky', top: 0 }} />
                {meses.map((mes, i) => (
                  <th
                    key={i}
                    colSpan={mes.span}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: Colors.white,
                      fontSize: 10,
                      fontWeight: 500,
                      color: Colors.neutral_400,
                      textTransform: 'capitalize',
                      textAlign: 'center',
                      padding: '2px 4px',
                      whiteSpace: 'nowrap',
                    }}>
                    {mes.span > 1 ? mes.etiqueta : ''}
                  </th>
                ))}
                {resumen && <th style={{ position: 'sticky', top: 0, backgroundColor: Colors.white }} />}
              </tr>
              <tr>
                <th
                  style={{
                    ...coopBase,
                    zIndex: 4,
                    position: 'sticky',
                    top: 18,
                    fontSize: 11,
                    color: Colors.gray_base,
                    borderBottom: `1px solid ${Colors.neutral_100}`,
                  }}>
                  Cooperativa
                </th>
                {semanas.map(semana => (
                  <th
                    key={semana.yw}
                    title={`Semana del ${semana.inicio}`}
                    style={{
                      position: 'sticky',
                      top: 18,
                      zIndex: 1,
                      backgroundColor: Colors.white,
                      fontSize: 10,
                      fontWeight: 400,
                      color: Colors.neutral_400,
                      textAlign: 'center',
                      minWidth: 34,
                      padding: '2px 0',
                      borderBottom: `1px solid ${Colors.neutral_100}`,
                    }}>
                    {semana.label}
                  </th>
                ))}
                {resumen && (
                  <th
                    style={{
                      position: 'sticky',
                      top: 18,
                      zIndex: 1,
                      backgroundColor: Colors.white,
                      fontSize: 11,
                      color: Colors.gray_base,
                      padding: '2px 10px',
                      textAlign: 'right',
                      borderBottom: `1px solid ${Colors.neutral_100}`,
                    }}>
                    {resumen.header}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {visibles.map(fila => (
                <tr key={fila.id_cooperativa}>
                  <td style={coopBase} title={fila.cooperativa}>
                    {fila.cooperativa}
                  </td>
                  {semanas.map(semana => {
                    const valor = fila.counts?.[semana.yw] || 0;
                    const nivel = escalon(valor);
                    return (
                      <td
                        key={semana.yw}
                        title={`${fila.cooperativa} · ${semana.label} ${semana.anio}: ${formato(valor, fila)}`}
                        style={{
                          ...celdaBase,
                          backgroundColor: nivel < 0 ? ChartColors.sequentialEmpty : ChartColors.sequential[nivel],
                          color: nivel < 0 ? Colors.neutral_200 : ChartColors.sequentialInk[nivel],
                        }}>
                        {valor ? formato(valor, fila) : ''}
                      </td>
                    );
                  })}
                  {resumen && (
                    <td
                      style={{
                        fontSize: 12,
                        padding: '3px 10px',
                        textAlign: 'right',
                        whiteSpace: 'nowrap',
                        color: Colors.neutral_800,
                      }}>
                      {resumen.valor(fila)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsoSirioHeatmap;
